import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MailCheck, MessageCircle, Mail, Rocket } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import { qk } from "../api/queries";
import type { TrialInfo } from "../api/types";
import { Brand } from "../app/Brand";
import { LocaleMenu } from "../app/LocaleMenu";
import { isLocale, setLocale, useLocale, useT } from "../i18n/core";
import { CONTACT, whatsappHref } from "../lib/contact";
import { useFormat } from "../lib/format";
import { LoadingRows } from "../ui/Common";
import { signupMessages } from "./signup.i18n";

/**
 * Cadastro público do teste gratuito (ADR 0038). A conta não nasce aqui: o
 * servidor manda ao e-mail o link do convite, e é nele que a pessoa cria a
 * senha e a verificação em duas etapas.
 */
export function SignupPage() {
  const api = useApi();
  const t = useT(signupMessages);
  const [params] = useSearchParams();
  const lang = params.get("lang");

  useEffect(() => {
    // O site manda ?lang= para a página abrir no idioma de quem clicou.
    if (isLocale(lang)) setLocale(lang);
  }, [lang]);

  const locale = useLocale();
  // Com ?lang=, espera o idioma trocar para não buscar os termos no idioma errado.
  const info = useQuery({
    queryKey: qk.trialInfo(locale),
    queryFn: () => api.getTrialInfo(),
    retry: false,
    enabled: !isLocale(lang) || lang === locale,
  });
  const [sentTo, setSentTo] = useState<string | null>(null);

  return (
    <div className="login">
      <aside className="login-side">
        <Brand />
        <div className="login-quote">
          <h2>{t.headline}</h2>
          <p>{t.pitch}</p>
        </div>
        <span />
      </aside>
      <div className="login-form">
        <div className="login-lang"><LocaleMenu variant="plain" /></div>
        <div className="login-card">
          {sentTo !== null ? (
            <Done email={sentTo} />
          ) : info.isPending ? (
            <>
              <h1>{t.title}</h1>
              <p className="ink-2" aria-live="polite">{t.loading}</p>
              <LoadingRows rows={4} />
            </>
          ) : info.isError ? (
            <Unavailable title={t.unavailableTitle} lede={t.unavailableLede} />
          ) : !info.data.available ? (
            <Unavailable title={t.soldOutTitle} lede={t.soldOutLede} />
          ) : (
            <SignupForm info={info.data} onSent={setSentTo} />
          )}
        </div>
      </div>
    </div>
  );
}

function SignupForm({ info, onSent }: { info: TrialInfo; onSent: (email: string) => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const t = useT(signupMessages);
  const f = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !email.trim() || !company.trim()) {
      setError(t.fieldsRequired);
      return;
    }
    if (!accept) {
      setError(t.acceptRequired);
      return;
    }
    setBusy(true);
    try {
      await api.trialSignup({
        name: name.trim(), email: email.trim(), company: company.trim(),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        locale, acceptTerms: true, website,
      });
      onSent(email.trim());
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.failed);
      // 503: as vagas podem ter acabado desde que a página abriu; sem vaga, a
      // página troca para a tela de esgotado, com os contatos.
      if (err instanceof ApiRequestError && err.status === 503) void qc.invalidateQueries({ queryKey: qk.trialInfo(locale) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate style={{ display: "grid", gap: 18 }}>
      <div>
        <h1>{t.title}</h1>
        <p className="ink-2" style={{ marginTop: 6 }}>{t.lede}</p>
      </div>
      <section aria-labelledby="signup-limits" className="callout">
        <Rocket aria-hidden="true" />
        <div style={{ display: "grid", gap: 4 }}>
          <strong id="signup-limits">{t.limits}</strong>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>{t.limitDays(info.days)}</li>
            <li>{t.limitSpace(f.gb(info.quotaBytes))}</li>
            <li>{t.limitMachines(info.machineLimit)}</li>
            <li>{t.limitGrace(info.graceDays)}</li>
          </ul>
        </div>
      </section>
      <div className="field">
        <label htmlFor="signup-name">{t.name}</label>
        <input id="signup-name" className="input" autoComplete="name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor="signup-email">{t.email}</label>
        <input id="signup-email" className="input" type="email" autoComplete="email" maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor="signup-company">{t.company}</label>
        <input id="signup-company" className="input" autoComplete="organization" maxLength={120} value={company} onChange={(e) => setCompany(e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor="signup-phone">{t.phone}</label>
        <input id="signup-phone" className="input" type="tel" autoComplete="tel" maxLength={30} value={phone} onChange={(e) => setPhone(e.target.value)} />
        <span className="hint">{t.phoneHint}</span>
      </div>
      {/* Campo-isca: invisível e fora da tabulação; só robô preenche, e o servidor descarta. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor="signup-website">{t.website}</label>
        <input id="signup-website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>
      <label className="check" htmlFor="signup-accept">
        <input id="signup-accept" type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} required />
        <span>
          {t.acceptBefore}
          <a href={info.termsUrl} target="_blank" rel="noopener noreferrer">{t.termsLink}</a>
          {t.acceptMiddle}
          <a href={info.privacyUrl} target="_blank" rel="noopener noreferrer">{t.privacyLink}</a>
          {t.acceptAfter}
        </span>
      </label>
      {error && <p className="field error" role="alert">{error}</p>}
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ height: 40 }}>
        <Rocket aria-hidden="true" /> {busy ? t.sending : t.submit}
      </button>
      <button className="btn btn-ghost" type="button" onClick={() => navigate("/login")}>{t.haveAccount}</button>
    </form>
  );
}

function Done({ email }: { email: string }) {
  const t = useT(signupMessages);
  const navigate = useNavigate();
  return (
    <div style={{ display: "grid", gap: 14 }} role="status">
      <MailCheck aria-hidden="true" size={32} style={{ color: "var(--accent-text)" }} />
      <h1>{t.doneTitle}</h1>
      <p className="ink-2">{t.doneLede(email)}</p>
      <p className="ink-2">{t.doneValid}</p>
      <p className="muted">{t.doneSpam}</p>
      <button className="btn" type="button" onClick={() => navigate("/login")}>{t.backToLogin}</button>
    </div>
  );
}

function Unavailable({ title, lede }: { title: string; lede: string }) {
  const t = useT(signupMessages);
  const navigate = useNavigate();
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <h1>{title}</h1>
      <p className="ink-2">{lede}</p>
      <ContactChannels />
      <button className="btn btn-ghost" type="button" onClick={() => navigate("/login")}>{t.haveAccount}</button>
    </div>
  );
}

/** WhatsApp e e-mail da equipe, com o endereço visível e selecionável. */
function ContactChannels() {
  const t = useT(signupMessages);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <a className="btn" href={whatsappHref()} target="_blank" rel="noopener noreferrer">
        <MessageCircle aria-hidden="true" /> {t.whatsapp}: <span style={{ userSelect: "all" }}>{CONTACT.whatsapp}</span>
      </a>
      <a className="btn btn-ghost" href={`mailto:${CONTACT.mail}`}>
        <Mail aria-hidden="true" /> {t.mail}: <span style={{ userSelect: "all" }}>{CONTACT.mail}</span>
      </a>
    </div>
  );
}
